import { BellOutlined, CloseOutlined } from "@ant-design/icons";
import { Badge, Button, Drawer, Flex, Typography } from "antd";
import { useUpdateAllNotificationsByBusinessIdToSeen } from "hooks/mutations/notification";
import {
  getNotificationScroll,
  getUnseenNotificationCount,
  NotificationCategory,
  useGetNotificationSections,
} from "hooks/queries/notification";
import { useState } from "react";
import { translate, translateWithVars } from "shared/languages";
import { unseenCountLabel } from "shared/utils";
import { colors } from "shared/theme/color";
import { Notification } from "shared/types";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_SUBMIT_FORMAT,
  localDateOnly,
  now,
} from "shared/utils/dayjs";
import { NotificationBlock } from "./NotificationBlock";
import { NotificationFilterPills } from "./NotificationFilterPills";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// Quiet 30px square icon action in the drawer header (prototype .icon-btn).
const headerIconButtonStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  color: colors.ink3,
};

// Uncontrolled by default (the bell owns its open state). A parent that has
// another way in (the dashboard's empty-state "Lihat Semua" link) passes
// `open` + `onOpenChange` and the bell and the link share one drawer.
export const NotificationDrawer = ({
  open: openProp,
  onOpenChange,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) => {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setIsOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [category, setCategory] = useState<NotificationCategory>("all");
  const { data: unseen } = getUnseenNotificationCount();
  const {
    data: notificationData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isSuccess,
  } = getNotificationScroll(open, category);
  const { mutate: updateNotificationsToSeen } =
    useUpdateAllNotificationsByBusinessIdToSeen();
  const { map: notificationMap, size: notificationCount } =
    useGetNotificationSections(notificationData);
  const todayKey = now().format(DEFAULT_DATE_SUBMIT_FORMAT);

  // Closing the drawer marks what it was showing as seen: the active filter
  // pill's category only (business-wide under "Semua"), since a notification
  // under another pill was never on screen.
  const closeDrawer = () => {
    setIsOpen(!open);
    if (unseen && unseen > 0) {
      updateNotificationsToSeen(category);
    }
  };

  return (
    <>
      <Badge
        count={unseen}
        color="red"
        overflowCount={99}
        size={"small"}
        offset={[-4, 5]}
      >
        {/* Icon-only: without its own label the button's name is the
            icon's built-in "bell", which says nothing about the badge. */}
        <Button
          icon={<BellOutlined />}
          aria-label={
            unseen
              ? translateWithVars("notification.bell_label_unseen", { n: unseen })
              : translate("notification.name")
          }
          style={{ borderRadius: 30 }}
          onClick={() => setIsOpen(!open)}
        />
      </Badge>
      <Drawer
        open={open}
        onClose={closeDrawer}
        closable={false}
        title={
          <Flex align="center" gap={8}>
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 600,
                fontSize: textPx(18),
                letterSpacing: "-0.01em",
              }}
            >
              {translate("notification.name")}
            </span>
            {(unseen ?? 0) > 0 && (
              <span
                style={{
                  fontSize: textPx(11),
                  fontWeight: 600,
                  background: colors.navyTint,
                  color: colors.navyDark,
                  padding: "1px 8px",
                  borderRadius: 999,
                }}
              >
                {unseenCountLabel(unseen ?? 0)}
              </span>
            )}
          </Flex>
        }
        extra={
          <Button
            type="text"
            icon={<CloseOutlined />}
            aria-label={translate("button.close")}
            style={headerIconButtonStyle}
            onClick={closeDrawer}
          />
        }
        width={400}
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div
          id="scrollableDiv"
          style={{
            flex: 1,
            overflow: "auto",
            padding: "12px 14px 16px",
          }}
        >
          <div style={{ marginBottom: 6 }}>
            <NotificationFilterPills value={category} onChange={setCategory} />
          </div>
          <Flex gap={6} vertical>
            {Array.from(notificationMap.entries())
              .sort(([a], [b]) => (a < b ? 1 : -1))
              .map(([dateKey, items]) => {
                // "Hari ini" for today; DD/MM/YYYY for every other date.
                // (A bare "today" key doesn't exist: i18next would echo the
                // raw key back, which rendered as "Today".) The key is a bare
                // Jakarta date, so it is compared as a string and formatted
                // with localDateOnly: tzDate would read it in the browser's
                // zone and, east of Jakarta (Bali, Singapore), file today's
                // rows under yesterday's date.
                const dateKeyDisplay =
                  dateKey === todayKey
                    ? translate("schedule.feature.today")
                    : localDateOnly(dateKey).format(DEFAULT_DATE_FORMAT);

                return (
                  <Flex key={dateKey} vertical gap={9}>
                    <Text
                      style={{
                        fontSize: textPx(11),
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: colors.ink3,
                        padding: "12px 6px 0",
                      }}
                    >
                      {dateKeyDisplay}
                    </Text>

                    {items.map((item: Notification) => (
                      <NotificationBlock
                        key={item.id}
                        notification={item}
                        closeDrawer={() => setIsOpen(!open)}
                      />
                    ))}
                  </Flex>
                );
              })}
          </Flex>
          {/* Without this a filter with no rows left the drawer blank below
              the pills. Shown only once the first page has loaded, so a
              failed request never claims there is nothing. */}
          {isSuccess && notificationCount === 0 && (
            <Flex
              vertical
              align="center"
              gap={10}
              style={{ padding: "48px 16px 24px", textAlign: "center" }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: colors.bgSoft,
                  color: colors.ink3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: textPx(18),
                }}
              >
                <BellOutlined />
              </div>
              <Text style={{ color: colors.ink2 }}>
                {category === "all"
                  ? translate("notification.empty.all")
                  : translateWithVars("notification.empty.filtered", {
                      name: translate(`notification.filter.${category}`),
                    })}
              </Text>
              {category !== "all" && (
                <Button
                  type="link"
                  onClick={() => setCategory("all")}
                  style={{
                    padding: 0,
                    height: "auto",
                    fontWeight: 600,
                    color: colors.navy,
                  }}
                >
                  {translate("notification.see_all")}
                </Button>
              )}
            </Flex>
          )}
          {hasNextPage && (
            <Flex justify={"center"} style={{ marginTop: 24 }}>
              <Button
                type="primary"
                loading={isFetchingNextPage}
                onClick={() => fetchNextPage()}
                style={{ width: 300, borderRadius: 20 }}
              >
                {translate("button.load_more")}
              </Button>
            </Flex>
          )}
        </div>
      </Drawer>
    </>
  );
};
